/**
 * AdminSecurityConsole — Super-admin inventory + password rotation.
 *
 * Route: /admin/security/console
 */
import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Shield, KeyRound, AlertTriangle, ClockAlert, RefreshCw, Copy, ShieldAlert, Wallet,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const StatCard = ({ label, value, tone = 'slate', testid, icon: Icon }) => {
  const tones = {
    slate: 'from-slate-500/15 to-slate-500/5 text-slate-300 border-slate-400/30',
    rose: 'from-rose-500/15 to-rose-500/5 text-rose-300 border-rose-400/30',
    amber: 'from-amber-500/15 to-amber-500/5 text-amber-300 border-amber-400/30',
  };
  return (
    <div data-testid={testid} className={`rounded-xl border bg-gradient-to-br p-4 ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider opacity-80">{label}</span>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <div className="mt-2 text-2xl font-semibold">{value ?? '—'}</div>
    </div>
  );
};

export default function AdminSecurityConsole() {
  const [copied, setCopied] = useState(null);

  const inv = useQuery({
    queryKey: ['admins-inventory'],
    queryFn: async () => {
      const r = await axios.get(`${API}/api/admin/security/admins-inventory`, { headers: authH() });
      return r.data;
    },
  });

  const rotateOne = useMutation({
    mutationFn: async ({ id, reason }) => {
      const r = await axios.post(
        `${API}/api/admin/security/admins/${id}/force-password-reset`,
        { reason },
        { headers: { ...authH(), 'Content-Type': 'application/json' } },
      );
      return r.data;
    },
    onSuccess: (data) => {
      toast.success(`Reset issued for ${data.target_email}`);
      setCopied(data.reset_token);
      inv.refetch();
    },
    onError: (e) => toast.error(`Reset failed: ${e?.response?.data?.detail || e.message}`),
  });

  const rotateDefaults = useMutation({
    mutationFn: async () => {
      const r = await axios.post(
        `${API}/api/admin/security/admins/rotate-defaults`,
        {},
        { headers: authH() },
      );
      return r.data;
    },
    onSuccess: (data) => {
      toast.success(`Rotated ${data.rotated_count} default-creds admin(s)`);
      inv.refetch();
    },
    onError: (e) => toast.error(`Rotate failed: ${e?.response?.data?.detail || e.message}`),
  });

  const financeToggle = useQuery({
    queryKey: ['super-admin-finance-toggle'],
    queryFn: async () => {
      const r = await axios.get(`${API}/api/admin/security/super-admin-finance-toggle`, { headers: authH() });
      return r.data;
    },
  });

  const setFinanceToggle = useMutation({
    mutationFn: async (value) => {
      const r = await axios.put(
        `${API}/api/admin/security/super-admin-finance-toggle`,
        { value },
        { headers: { ...authH(), 'Content-Type': 'application/json' } },
      );
      return r.data;
    },
    onSuccess: (data) => {
      toast.success(`Super-admin finance access set to: ${data.value}`);
      financeToggle.refetch();
    },
    onError: (e) => toast.error(`Toggle failed: ${e?.response?.data?.detail || e.message}`),
  });

  const items = inv.data?.items || [];
  const summary = inv.data?.summary || {};

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-100" data-testid="admin-security-console-page">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="flex items-center gap-3 text-3xl font-bold">
              <ShieldAlert className="h-7 w-7 text-rose-400" />
              Security Console
            </h1>
            <p className="mt-1 text-sm text-slate-400">
              Rotate admin passwords · force reset outstanding JWTs (SEC-003) · one-click sweep for
              accounts still on the seeded default credentials.
            </p>
          </div>
          <Button
            data-testid="sec-console-refresh"
            variant="outline"
            className="border-slate-700 bg-slate-900 hover:bg-slate-800"
            onClick={() => inv.refetch()}
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard testid="stat-total" label="Admins" value={summary.total} icon={Shield} />
          <StatCard testid="stat-default" label="Default creds" value={summary.default_creds} tone="rose" icon={AlertTriangle} />
          <StatCard testid="stat-stale" label="Stale (>90d)" value={summary.stale} tone="amber" icon={ClockAlert} />
          <StatCard testid="stat-needs" label="Needs rotation" value={summary.needs_rotation} tone="amber" icon={KeyRound} />
        </div>

        {summary.default_creds > 0 && (
          <Card className="border-rose-500/40 bg-rose-500/10">
            <CardContent className="flex items-center justify-between gap-4 p-4">
              <div>
                <p className="text-sm font-semibold text-rose-200">
                  {summary.default_creds} admin still using the seeded default password
                </p>
                <p className="mt-1 text-xs text-rose-300/80">
                  The default `admin@eventflow.com / password` was in the source before the SEC-001 fix. Rotate now.
                </p>
              </div>
              <Button
                data-testid="rotate-defaults-btn"
                className="bg-rose-600 text-white hover:bg-rose-500"
                onClick={() => rotateDefaults.mutate()}
                disabled={rotateDefaults.isPending}
              >
                {rotateDefaults.isPending ? 'Rotating…' : 'Rotate all default-creds admins'}
              </Button>
            </CardContent>
          </Card>
        )}

        {copied && (
          <Card className="border-emerald-500/40 bg-emerald-500/5">
            <CardContent className="flex items-center justify-between gap-4 p-4">
              <div>
                <p className="text-sm font-semibold text-emerald-200">Reset token issued</p>
                <p className="mt-1 font-mono text-xs text-emerald-300 break-all">{copied}</p>
              </div>
              <Button
                variant="outline"
                className="border-emerald-500/40 bg-emerald-500/5 text-emerald-200 hover:bg-emerald-500/10"
                onClick={() => { navigator.clipboard.writeText(copied); toast.success('Token copied'); }}
              >
                <Copy className="mr-2 h-4 w-4" /> Copy
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="border-slate-800 bg-slate-900/60" data-testid="super-admin-finance-toggle-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="h-4 w-4 text-violet-300" />
              Super-Admin · Finance Access Toggle
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-400">
              Least-privilege control: decide whether super-admins automatically inherit
              finance permissions. Explicit <code className="rounded bg-slate-800 px-1 text-[10px]">finance_roles</code> are always honoured.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {['auto', 'restricted', 'off'].map((v) => {
                const active = financeToggle.data?.value === v;
                return (
                  <Button
                    key={v}
                    data-testid={`finance-toggle-${v}`}
                    onClick={() => setFinanceToggle.mutate(v)}
                    disabled={setFinanceToggle.isPending}
                    variant={active ? 'default' : 'outline'}
                    className={
                      active
                        ? 'bg-violet-600 text-white hover:bg-violet-500'
                        : 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800'
                    }
                  >
                    {v === 'auto' && 'Auto (legacy)'}
                    {v === 'restricted' && 'Restricted (read-only)'}
                    {v === 'off' && 'Off (no finance)'}
                    {active ? ' ✓' : ''}
                  </Button>
                );
              })}
              <span className="ml-2 text-xs text-slate-500" data-testid="finance-toggle-source">
                source: {financeToggle.data?.source || '—'}
              </span>
            </div>
            <div className="rounded border border-slate-800 bg-slate-950 p-3 text-xs text-slate-300">
              {financeToggle.data?.options?.[financeToggle.data?.value] || 'Loading…'}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Admins ({summary.total || 0})</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="admins-table">
              <thead>
                <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-400">
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Role</th>
                  <th className="py-2 pr-4">Last login</th>
                  <th className="py-2 pr-4">Password age</th>
                  <th className="py-2 pr-4">Flags</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {inv.isLoading && (
                  <tr><td colSpan={6} className="py-8 text-center text-slate-500">Loading…</td></tr>
                )}
                {items.map((a) => (
                  <tr key={a.id} className="border-b border-slate-800/60" data-testid="admin-row">
                    <td className="py-2 pr-4">
                      <div className="font-medium">{a.email}</div>
                      {a.name && <div className="text-xs text-slate-500">{a.name}</div>}
                    </td>
                    <td className="py-2 pr-4">
                      <Badge variant="outline" className="border-slate-700 text-slate-300">{a.role || 'admin'}</Badge>
                    </td>
                    <td className="py-2 pr-4 text-slate-400">
                      {a.last_login_at ? new Date(a.last_login_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-2 pr-4 text-slate-400">
                      {a.password_age_days !== null ? `${a.password_age_days}d` : '—'}
                    </td>
                    <td className="py-2 pr-4">
                      <div className="flex flex-wrap gap-1">
                        {a.is_default_creds && (
                          <Badge className="bg-rose-500/20 text-rose-300" data-testid="flag-default">default</Badge>
                        )}
                        {a.is_stale && (
                          <Badge variant="outline" className="border-amber-500/40 text-amber-300" data-testid="flag-stale">stale</Badge>
                        )}
                        {a.needs_rotation && (
                          <Badge variant="outline" className="border-amber-500/40 text-amber-300">needs rotation</Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-4">
                      <Button
                        data-testid={`force-reset-${a.id}`}
                        size="sm"
                        variant="outline"
                        className="border-slate-700 bg-slate-900 hover:bg-slate-800"
                        onClick={() => rotateOne.mutate({ id: a.id, reason: 'super-admin console' })}
                      >
                        Force reset
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
